import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Icon, Input } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import {
    TbArrowRight,
    TbHeadphones,
    TbHelp,
    TbLifebuoy,
    TbMicrophone,
    TbNetwork,
    TbPhoneCall,
    TbSearch,
    TbSettings,
} from "react-icons/tb";

import { type HelpCenterIconKey, useHelpCenterProblems } from "@/queries/strapi/help-center.queries";
import { APP_ROUTES_TO } from "@/utils/routes";

export const Route = createFileRoute("/help-center/")({
    component: HelpCenterPage,
    staticData: {
        config: {
            title: "help_center",
        },
    },
});

const HELP_GUIDE_ICON_MAP: Record<HelpCenterIconKey, IconType> = {
    phone: TbPhoneCall,
    headphones: TbHeadphones,
    microphone: TbMicrophone,
    lifebuoy: TbLifebuoy,
    settings: TbSettings,
    network: TbNetwork,
    general: TbHelp,
};

function HelpCenterPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const {
        data: remoteGuides,
        isError: isRemoteGuidesError,
        isSuccess: isRemoteGuidesSuccess,
    } = useHelpCenterProblems();

    const guides = remoteGuides ?? [];

    const filteredGuides = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (!normalizedSearch) {
            return guides;
        }

        return guides.filter((guide) => {
            const titleMatches = guide.title.toLowerCase().includes(normalizedSearch);
            const descriptionMatches = guide.description.toLowerCase().includes(normalizedSearch);
            const keywordMatches = guide.keywords?.toLowerCase().includes(normalizedSearch) ?? false;
            const stepMatches = guide.steps.some((step) => step.text.toLowerCase().includes(normalizedSearch));

            return titleMatches || descriptionMatches || keywordMatches || stepMatches;
        });
    }, [guides, searchTerm]);

    const showNoResults = isRemoteGuidesSuccess && filteredGuides.length === 0;

    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
            {isRemoteGuidesError ? (
                <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                    <p className="text-sm font-semibold">{t("help_center_fetch_failed_title")}</p>
                    <p className="mt-1 text-sm">{t("help_center_fetch_failed_description")}</p>
                </div>
            ) : null}

            <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-linear-to-b from-primary/10 to-transparent" />
                <div className="relative">
                    <div className="flex items-center justify-between">
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1 text-xs font-medium text-muted-foreground">
                            <Icon icon={TbLifebuoy} />
                            {t("quick_support")}
                        </div>
                        <div className="w-full max-w-md">
                            <div className="relative">
                                <Icon
                                    icon={TbSearch}
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                />
                                <Input
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder={t("search_your_question_or_problem")}
                                    className="pl-10"
                                />
                            </div>
                        </div>
                    </div>
                    <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("help_center_intro")}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{t("help_center_contact_support")}</p>
                    <div className="mt-4">
                        <Button
                            variant="outline"
                            className="group"
                            onClick={() => {
                                void navigate({ to: APP_ROUTES_TO.SETTINGS });
                            }}
                        >
                            {t("open_settings_page")}
                            <Icon
                                icon={TbArrowRight}
                                className="ml-2 transition-transform duration-200 group-hover:translate-x-0.5"
                            />
                        </Button>
                    </div>
                </div>
            </div>

            {showNoResults ? (
                <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center">
                    <p className="text-sm font-medium">{t("help_center_no_results_title")}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{t("help_center_no_results_description")}</p>
                </div>
            ) : null}

            {isRemoteGuidesSuccess && filteredGuides.length > 0 ? (
                <Accordion type="single" collapsible className="space-y-3">
                    {filteredGuides.map((guide) => (
                        <AccordionItem
                            key={guide.id}
                            value={guide.id}
                            className="overflow-hidden rounded-xl border border-border bg-card px-1 transition-all duration-300 hover:border-primary/30 hover:shadow-sm"
                        >
                            <AccordionTrigger className="px-4 py-4 hover:no-underline">
                                <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <Icon icon={HELP_GUIDE_ICON_MAP[guide.icon]} size={20} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="truncate text-base font-semibold">{guide.title}</p>
                                        <p className="truncate text-sm text-muted-foreground">{guide.description}</p>
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                                <ol className="space-y-2 pt-1">
                                    {guide.steps.map((step, index) => (
                                        <li
                                            key={`${guide.id}-${step.id}`}
                                            className="rounded-lg border border-border/70 bg-background px-3 py-2.5 text-sm transition-colors duration-200 hover:bg-muted/40"
                                        >
                                            <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                                                {index + 1}
                                            </span>
                                            {step.text}
                                        </li>
                                    ))}
                                </ol>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            ) : null}
        </div>
    );
}
